import './styles.scoped.css'
import {Typography, Button, IconButton, Modal, Avatar, Chip} from '@mui/material'
import {Close, DateRange, AccessTime} from '@mui/icons-material'
import {useMobile} from '../../hooks'
import {getColorFromName, getInitials, ignoreTimezone, parseError, formatTime} from '../../utils/helpers'
import {format} from 'date-fns'
import {scheduleApi} from '../../services'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'
import {VisitDialogCardSkeleton} from '../Skeletons'

export default ({open, setOpen, loadData, items = [], loading = false}) => {
    const {mobile} = useMobile()
    const {setLoading, setMessageBox, branchId} = useGlobalStates()

    const submit = () => {
        setLoading(true)
        scheduleApi
            .undoClockIn(branchId, items?.[0]?.schedule_id, {visit_ids: items?.map(item => item?.id)})
            .then(() => {
                setLoading(false)
                setOpen(false)
                if (items?.length == 1) {
                    setMessageBox({
                        status: 'success',
                        title: 'Check-In Successfully Undone',
                        description: `The appointment for ${items[0]?.schedule?.caregiver?.first_name + ' ' + items[0]?.schedule?.caregiver?.last_name} on ${format(new Date(ignoreTimezone(items[0]?.date)), 'EEE, dd MMM yyyy')} at ${items[0]?.start_time} - ${items[0]?.start_time} has been reverted to scheduled status.`,
                    })
                } else if (items?.length > 1) {
                    setMessageBox({
                        status: 'success',
                        title: 'Check-Ins Successfully Undone',
                        description: `Check-ins for ${items?.length} schedules have been successfully reverted to scheduled status.`,
                    })
                }
                loadData()
            })
            .catch(error => {
                setLoading(false)
                setOpen(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }

    return (
        <Modal open={open}>
            <div className="container">
                <div className="content">
                    {mobile ? (
                        <>
                            <div className="header">
                                <Typography variant="h6" color="text.primary">
                                    Are you sure you want to undo the clock this schedule{items?.length > 1 ? 's' : ''}?
                                </Typography>
                                <IconButton onClick={() => setOpen(false)}>
                                    <Close />
                                </IconButton>
                            </div>
                            <div className="actions">
                                <Button variant="outlined" onClick={() => setOpen(false)} style={{flex: 1}}>
                                    Keep Clock in
                                </Button>
                                <Button variant="contained" color="error" fullWidth onClick={submit} disabled={loading} style={{flex: 1}}>
                                    Undo Check-In {items?.length > 1 ? `(${items?.length})` : ''}
                                </Button>
                            </div>
                            <br />
                            <br />
                            <br />
                        </>
                    ) : (
                        <Typography variant="h6" color="text.primary" className="title">
                            Are you sure you want to undo the clock this schedule{items?.length > 1 ? 's' : ''}?
                        </Typography>
                    )}
                    <br />
                    <Typography variant="body2" color="text.primary" className="title">
                        Once you undo the check-in, the appointment will return to scheduled status.
                    </Typography>
                    {items.map((item, index) =>
                        loading ? (
                            <div key={index}>
                                <br />
                                <VisitDialogCardSkeleton />
                            </div>
                        ) : (
                            <div key={index}>
                                <br />
                                <div className="card">
                                    <div className="card-profile">
                                        <div className="people-card">
                                            {item?.schedule?.client?.avatar_url ? (
                                                <Avatar src={item?.schedule?.client?.avatar_url} style={{width: 40, height: 40}} />
                                            ) : (
                                                <Avatar
                                                    style={{
                                                        backgroundColor: getColorFromName(
                                                            item?.schedule?.client?.first_name + ' ' + item?.schedule?.client?.last_name,
                                                        ),
                                                        width: 40,
                                                        height: 40,
                                                        fontSize: 20,
                                                    }}>
                                                    {getInitials(item?.schedule?.client?.first_name + ' ' + item?.schedule?.client?.last_name)}
                                                </Avatar>
                                            )}
                                            <div className="people-profile-card">
                                                <Typography variant="h8" color="text.primary" className="name">
                                                    {item?.schedule?.client?.first_name + ' ' + item?.schedule?.client?.last_name}
                                                </Typography>
                                                <Typography variant="body3" color="text.secondary" className="name">
                                                    {item?.schedule?.service?.name}
                                                </Typography>
                                            </div>
                                        </div>
                                        {item?.status === 'scheduled' && (
                                            <Chip
                                                label="Scheduled"
                                                variant="filled"
                                                sx={theme => ({
                                                    backgroundColor: theme.palette.grey[700],
                                                    color: theme.palette.secondary.contrastText,
                                                })}
                                                size="small"
                                            />
                                        )}
                                        {item?.status === 'in_progress' && <Chip label="In progress" variant="filled" color="warning" size="small" />}
                                        {item?.status === 'ended' && (
                                            <Chip
                                                label="Ended"
                                                variant="filled"
                                                sx={theme => ({
                                                    backgroundColor: '#02CAD1',
                                                    color: theme.palette.primary.contrastText,
                                                })}
                                                size="small"
                                            />
                                        )}
                                        {item?.status === 'finished' && <Chip label="Finished" variant="filled" color="info" size="small" />}
                                        {item?.status === 'completed' && <Chip label="Completed" variant="filled" color="success" size="small" />}
                                        {item?.status === 'cancelled' && <Chip label="Cancelled" variant="filled" color="error" size="small" />}
                                    </div>
                                    <br />
                                    <div className="datetime">
                                        <div className="row-content">
                                            <Typography variant="body3" color="text.secondary">
                                                <DateRange fontSize="small" />
                                            </Typography>
                                            <Typography variant="body3" color="text.secondary">
                                                {format(new Date(ignoreTimezone(item?.date)), 'EEE, dd MMM yyyy')}
                                            </Typography>
                                        </div>
                                        <Typography variant="body3" color="text.secondary">
                                            |
                                        </Typography>
                                        <div className="row-content">
                                            <Typography variant="body3" color="text.secondary">
                                                <AccessTime fontSize="small" />
                                            </Typography>
                                            <Typography variant="body3" color="text.secondary">
                                                {formatTime(item?.start_time)} - {formatTime(item?.end_time)}
                                            </Typography>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ),
                    )}
                    <br />
                    {mobile ? (
                        <>
                            <br />
                            <br />
                            <br />
                            <br />
                        </>
                    ) : (
                        <div className="actions">
                            <Button variant="outlined" fullWidth onClick={() => setOpen(false)}>
                                Keep Clock in
                            </Button>
                            <Button variant="contained" color="error" fullWidth onClick={submit} disabled={loading}>
                                Undo Check-In {items?.length > 1 ? `(${items?.length})` : ''}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    )
}
